import Decimal from 'decimal.js'

const MIN_SLICE_S = 10
const DEFAULT_SLICES = 10

// Market clips per slice. Backend has no opinion on this — it's a client-chosen knob
// that trades off clip granularity against per-clip size; lowered automatically (see
// below) when the resulting order_size would fall under the instrument's min order size.
const DEFAULT_N_CLIPS = 10

// Worst-case fraction of each slice reserved for the catchup tail (backend schedules
// catchup to start at expires_at − intervals × interval_ms; this sizes that window).
const TAIL_FRAC = 0.3

export interface DeriveCatchupParamsInput {
  durationSeconds: number
  requestedTotalSlices: number
  /** Order quantity, in the same unit/denomination as the parent order's `quantity`. */
  quantity: number
  /** Instrument's quantity increment, same denomination as `quantity`. */
  qtyStep?: number
  /** Instrument's minimum order size, same denomination as `quantity`. */
  minOrderSize?: number
}

export interface DerivedCatchupParams {
  frequencyMs: number
  orderSize: string
  sliceSeconds: number
  totalSlices: number
}

function ceilToStep(value: Decimal, step: number | undefined): Decimal {
  if (!step || step <= 0) return value
  const stepD = new Decimal(step)
  return value.dividedBy(stepD).ceil().times(stepD)
}

/**
 * Auto-derives the catchup leg (frequency + order_size) from duration/total_slices/
 * quantity alone — no user input, per the #trading-desk Slack contract (2026-09-17,
 * mirroring services/algo/src/algos/advanced_twap.rs's resolve_slicing and
 * validate_catchup_fits_slice). The frontend never lets the user pick a catchup cadence
 * that undercuts this: it's computed fresh from the order's own shape every submit.
 *
 * Mirrors the backend's slice resolution EXACTLY (integer division, 10s floor) — using
 * float division here would compute a different slice_s than the backend for duration/
 * total_slices pairs that don't divide evenly, and the self-check below would pass
 * client-side while still failing server-side.
 *
 * Returns null only if the inputs can't produce a valid catchup leg at all (e.g. no
 * instrument qty step ever yields an order_size ≥ min order size) — callers should
 * treat that as a bug, not a user-facing validation error, since the user has no catchup
 * inputs to correct.
 */
export function deriveCatchupParams(input: DeriveCatchupParamsInput): DerivedCatchupParams | null {
  const { durationSeconds, requestedTotalSlices, quantity, qtyStep, minOrderSize } = input
  if (!(durationSeconds > 0) || !(quantity > 0)) return null

  const requested = requestedTotalSlices > 0 ? requestedTotalSlices : DEFAULT_SLICES
  const dur = Math.max(1, Math.floor(durationSeconds))
  const totalSlices =
    Math.floor(dur / requested) >= MIN_SLICE_S
      ? requested
      : Math.max(1, Math.floor(dur / MIN_SLICE_S))
  const sliceSeconds = Math.floor(dur / totalSlices)
  if (sliceSeconds <= 0) return null

  const perSliceQty = new Decimal(quantity).dividedBy(totalSlices)

  // Round the clip size up to the qty step, and back off n_clips (never round down —
  // that adds a clip and risks pushing the catchup start earlier than intended) until it
  // clears the instrument's min order size.
  let nClips = DEFAULT_N_CLIPS
  let orderSize = ceilToStep(perSliceQty.dividedBy(nClips), qtyStep)
  while (nClips > 1 && minOrderSize !== undefined && orderSize.lessThan(minOrderSize)) {
    nClips -= 1
    orderSize = ceilToStep(perSliceQty.dividedBy(nClips), qtyStep)
  }
  if (
    !orderSize.greaterThan(0) ||
    (minOrderSize !== undefined && orderSize.lessThan(minOrderSize))
  ) {
    return null
  }

  const windowMs = TAIL_FRAC * sliceSeconds * 1000
  const intervalMs = Math.floor(windowMs / (nClips + 1))
  if (!(intervalMs > 0)) return null

  // Self-check — same inequality the backend enforces (worst case: zero passive fills).
  const worstCaseS = (perSliceQty.dividedBy(orderSize).ceil().toNumber() + 1) * (intervalMs / 1000)
  if (worstCaseS > sliceSeconds) return null

  return {
    frequencyMs: intervalMs,
    orderSize: orderSize.toString(),
    sliceSeconds,
    totalSlices,
  }
}
